import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import Layout from '../components/Layout';
import DateRangePicker from '../components/Attendance/DateRangePicker';
import SearchBar from '../components/SearchBar';
import Pagination from '../components/Common/Pagination';
import CustomerInfoModal from '../components/Customer/CustomerInfoModal';
import CreateFollowUpModal from '../components/Followup/CreateFollowupModal';
import LogFollowupModal, { OutcomeType } from '../components/Followup/LogFollowupModal';
import QueryHistoryModal from '../components/Query/QueryHistoryModal';
import AssignmentModal from '../components/Query/AssignmentModal';
import AddBookingModal from '../components/Booking/AddBookingModal';
import VendorQuotationModal from '../components/Query/VendorQuotationModal';
// import ViewQuotationsModal from '../components/Quotation/ViewQuotationsModal';
import CreateQueryModal from '../components/Query/CreateQueryModal';
import CustomerPickerModal from '../components/Customer/CustomerPickerModal';
import { useRole } from '../hooks/usePermissions';
import { toast } from 'react-toastify';
import { isVendorRfqEnabled } from '../utils/vendorRfq';

import { Query } from '../types/Query';
import { Customer } from '../types/Customer';
import { User } from '../types/User';
import { fetchCustomerById } from '../api/Customer';
import { fetchQueries, fetchQueryById, updateQueryStatus } from '../api/Query';
import { fetchFollowups } from '../api/Followup';
import { extractLatestBookingDraft, materializeBookingDraftForQuery } from '../api/BookingDraft';
import { fetchUsers } from '../api/User';
import { formatDate, formatTime, parseDateValue } from '../utils/date';
import { QueryLane, getQueryLane, getQueryStatusValueForLane } from '../utils/queryStatus';
import { fromQueryRooms } from '../components/QuotationBuilder/quotationRoomOccupancy';

import '../css/Pages.css';
import '../css/PackagesPage.css';
import '../css/Kanban.css';
import '../css/modal.css';

import * as FiIcons from 'react-icons/fi';
import QueryCard from '../components/Query/QueryCard';
const FaPlus = FiIcons.FiPlus as unknown as React.FC;
const FiCalendar = FiIcons.FiCalendar as unknown as React.FC;

const PAGE_SIZE = 50;

const extractQueryList = (data: any): Query[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.queries)) return data.queries;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const mergeQueriesById = (existing: Query[], incoming: Query[]) => {
  if (!incoming.length) return existing;
  const existingIds = new Set(existing.map((item) => item.id).filter(Boolean));
  const toAppend = incoming.filter((item) => !item.id || !existingIds.has(item.id));
  return toAppend.length ? [...existing, ...toAppend] : existing;
};

const toDateKey = (value?: string) => {
  const date = parseDateValue(value);
  if (!date || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatFilterDate = (value: string) => {
  const date = parseDateValue(value);
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : value;
};

const getLast30DaysRange = () => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - 29);
  return { start: toDateKey(start.toISOString()), end: toDateKey(end.toISOString()) };
};

const QueriesPage = () => {
  const role = useRole();
  const vendorRfqEnabled = isVendorRfqEnabled();
  const hasFetched = useRef(false);
  const defaultDateRange = getLast30DaysRange();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // const [showViewFollowUpsModal, setShowViewFollowUpsModal] = useState(false);
  // const [showViewQuotationsModal, setShowViewQuotationsModal] = useState(false);
  const [showQueryHistory, setShowQueryHistory] = useState(false);
  const [showCreateFollowUpModal, setShowCreateFollowUpModal] = useState(false);
  const [followupRequired, setFollowupRequired] = useState(false);
  const [showLogFollowupModal, setShowLogFollowupModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showCreateBookingModal, setShowCreateBookingModal] = useState(false);
  const [selectedQueryForBooking, setSelectedQueryForBooking] = useState<Query | null>(null);
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [activeQuery, setActiveQuery] = useState<Query | null>(null);
  const [showVendorQuotation, setShowVendorQuotation] = useState(false);
  const [vendorQuotationQuery, setVendorQuotationQuery] = useState<Query | null>(null);

  const [queryId, setQueryId] = useState<number>(0);
  const [followupId, setFollowupId] = useState<number>(0);
  const [queries, setQueries] = useState<Query[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateQueryModal, setShowCreateQueryModal] = useState(false);
  const [showCustomerPicker, setShowCustomerPicker] = useState(false);
  const [newQueryCustomerId, setNewQueryCustomerId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [serverPaginated, setServerPaginated] = useState(false);
  const [serverTotalPages, setServerTotalPages] = useState(1);
  const [prefetchSeed, setPrefetchSeed] = useState(0);
  const [nextFollowupMap, setNextFollowupMap] = useState<Record<number, string>>({});
  const loadedServerPagesRef = useRef<Set<number>>(new Set());

  const [searchParams, setSearchParams] = useSearchParams();

  const [typeFilter, setTypeFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [updatedStartDateFilter, setUpdatedStartDateFilter] = useState(defaultDateRange.start);
  const [updatedEndDateFilter, setUpdatedEndDateFilter] = useState(defaultDateRange.end);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<'workedToday' | 'toBeWorked', boolean>>({
    workedToday: false,
    toBeWorked: false,
  });
  const navigate = useNavigate();
  const customerIdFromParams = parseInt(searchParams.get('customerId') || '0');

  const load = useCallback(async (customerId?: number) => {
    try {
      setLoading(true);
      const data = await fetchQueries(customerId || 0, { page: 1, pageSize: PAGE_SIZE });
      const list = extractQueryList(data);
      const pagination = (data as any)?.pagination || {};
      const hasPaginationMeta = !Array.isArray(data) && Boolean(
        (data as any)?.pagination ||
        (data as any)?.totalPages ||
        (data as any)?.page ||
        (data as any)?.total ||
        pagination?.totalPages ||
        pagination?.pages,
      );
      const total = Number((data as any)?.total ?? pagination?.total ?? pagination?.count ?? list.length);
      const resolvedTotalPages = Number(
        (data as any)?.totalPages ??
        pagination?.totalPages ??
        pagination?.pages ??
        Math.max(1, Math.ceil(total / PAGE_SIZE)),
      );

      setQueries(list);
      setPage(1);
      if (hasPaginationMeta) {
        setServerPaginated(true);
        setServerTotalPages(Number.isFinite(resolvedTotalPages) && resolvedTotalPages > 0 ? resolvedTotalPages : 1);
        loadedServerPagesRef.current = new Set([1]);
      } else {
        setServerPaginated(false);
        setServerTotalPages(Math.max(1, Math.ceil(list.length / PAGE_SIZE)));
        loadedServerPagesRef.current = new Set();
      }
      setPrefetchSeed((prev) => prev + 1);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const users = await fetchUsers();
      setAllUsers(Array.isArray(users) ? users : []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  }, []);

  const loadNextFollowups = useCallback(async () => {
    try {
      const data = await fetchFollowups();
      const list = Array.isArray(data) ? data : [];
      const now = new Date();
      const nextByQuery = new Map<number, Date>();
      list.forEach((f: any) => {
        const rawDate = String(f?.date || f?.followupDate || '').trim();
        if (!rawDate) return;
        const timeStrRaw: string = String(f?.time || '').trim();
        const timeStr = timeStrRaw ? timeStrRaw.substring(0, 5) : '00:00';
        const datePart = rawDate.includes('T') ? rawDate.split('T')[0] : rawDate.substring(0, 10);
        const composed = `${datePart}T${timeStr}`;
        const d = new Date(composed);
        if (isNaN(d.getTime())) return;
        const qid = Number(f?.queryId || f?.query_id);
        if (!qid) return;
        if (d < now) return;
        const existing = nextByQuery.get(qid);
        if (!existing || d < existing) nextByQuery.set(qid, d);
      });
      const map: Record<number, string> = {};
      nextByQuery.forEach((d, qid) => {
        map[qid] = d.toISOString();
      });
      setNextFollowupMap(map);
    } catch {
      setNextFollowupMap({});
    }
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    setPage(1);
    load(customerIdFromParams);
    loadUsers();
    loadNextFollowups();
  }, [customerIdFromParams, load, loadUsers, loadNextFollowups]);

  useEffect(() => {
    if (!serverPaginated || serverTotalPages <= 1) return;

    let cancelled = false;
    const run = async () => {
      for (let nextPage = 2; nextPage <= serverTotalPages; nextPage += 1) {
        if (cancelled) return;
        if (loadedServerPagesRef.current.has(nextPage)) continue;
        try {
          const data = await fetchQueries(customerIdFromParams || 0, { page: nextPage, pageSize: PAGE_SIZE });
          if (cancelled) return;
          const pageList = extractQueryList(data);
          setQueries((prev) => mergeQueriesById(prev, pageList));
          loadedServerPagesRef.current.add(nextPage);
          setPage((prev) => Math.max(prev, nextPage));
        } catch {
          return;
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [prefetchSeed, serverPaginated, serverTotalPages, customerIdFromParams]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, typeFilter, assigneeFilter, updatedEndDateFilter, updatedStartDateFilter]);

  const openCreateFollowUpModal = (queryId?: number) => {
    setQueryId(queryId || 0);
    setFollowupRequired(false);
    setShowCreateFollowUpModal(true);
  };

  const openShareQuotationModal = (queryOrId?: Query | number) => {
    const query = typeof queryOrId === 'object' ? queryOrId : queries.find(q => q.id === queryOrId) || activeQuery || null;
    const id = typeof queryOrId === 'number' ? queryOrId : query?.id || 0;
    setQueryId(id);
    void (async () => {
      const latestDraft = id ? await extractLatestBookingDraft(id) : null;
      const lastSharedQuotationAt = latestDraft?.updated_at || latestDraft?.updatedAt || latestDraft?.created_at || latestDraft?.createdAt;

      navigate('/quotation/builder', {
        state: {
          queryId: id,
          hotelRooms: fromQueryRooms(query?.rooms),
          quotationStep: 'builder',
          ...(lastSharedQuotationAt ? { lastSharedQuotationAt: String(lastSharedQuotationAt) } : {}),
        },
      });
    })();
  };

  const viewFollowUps = (queryId?: number) => {
    setQueryId(queryId || 0);
    setShowQueryHistory(true);
  };


  const openCustomerModal = async (customerId?: number) => {
    if (!customerId) return;
    try {
      const customer = await fetchCustomerById(customerId);
      setSelectedCustomer(customer);
    } catch (err) {
    }
  };

  const openAssignmentModal = (queryId?: number) => {
    setQueryId(queryId || 0);
    setShowAssignmentModal(true);
  };

  const openBookingModal = async (targetQueryId: number) => {
    let query = queries.find(q => q.id === targetQueryId) || null;
    if (!query) {
      try {
        query = await fetchQueryById(targetQueryId);
      } catch (error: any) {
        toast.error(error?.message || 'Failed to load query for booking');
        return;
      }
    }
    setSelectedQueryForBooking(query);
    setShowCreateBookingModal(true);
  };

  const openQueryModal = (query: Query) => {
    setActiveQuery(query);
    setShowQueryModal(true);
  };

  const openVendorQuotationModal = async (queryId: number) => {
    if (!vendorRfqEnabled) return;
    let query = queries.find(q => q.id === queryId) || null;
    if (!query && activeQuery?.id === queryId) query = activeQuery;
    if (!query) {
      try {
        query = await fetchQueryById(queryId);
      } catch (error: any) {
        toast.error(error?.message || 'Failed to load query for vendor quotation');
        return;
      }
    }
    setVendorQuotationQuery(query);
    setShowVendorQuotation(true);
  };

  const handleBookingCreated = async (query: Query, alreadyMaterialized = false) => {
    try {
      if (!alreadyMaterialized) {
        const order = await materializeBookingDraftForQuery(query.id);
        if (!order) throw new Error('Package-order draft was not found after booking creation');
      }
      const updated = await updateQueryStatus(query.id, 'BOOKED');
      setQueries(prev => prev.map(q => (q.id === query.id ? { ...q, status: updated.status } : q)));
      setActiveQuery(prev => (prev?.id === query.id ? { ...prev, status: updated.status } : prev));
      setSelectedQueryForBooking(prev => (prev?.id === query.id ? { ...prev, status: updated.status } : prev));
      setVendorQuotationQuery(prev => (prev?.id === query.id ? { ...prev, status: updated.status } : prev));
      toast.success('Booking created and query marked as booked');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update query status');
    }
  };

  const handleOutcomeAction = async (outcome: OutcomeType, queryId: number) => {
    if (outcome === 'followup') {
      openCreateFollowUpModal(queryId);
    } else if (outcome === 'quotation') {
      openShareQuotationModal(queryId);
    } else if (outcome === 'booked') {
      await openBookingModal(queryId);
    }
    setFollowupId(0);
  };

  const handleAssign = async (queryId: number, userId: number) => {
    setQueries(prev =>
      prev.map(query =>
        query.id === queryId ? { ...query, assigned_to: userId } : query
      )
    );
  };

  const filteredQueries = useMemo(() => (
    (Array.isArray(queries) ? queries : []).filter(query => {
      const customerName = (query.customer?.name || '').toLowerCase();
      const referenceId = (query.referenceId || '').toLowerCase();
      const matchesSearch = customerName.includes(searchTerm.toLowerCase()) ||
        query.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        query.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        referenceId.includes(searchTerm.toLowerCase());

      const matchesType = typeFilter ? query.type === typeFilter : true;
      const matchesAssignee = assigneeFilter ? query.assignedTo?.id?.toString() === assigneeFilter : true;
      const updatedDate = toDateKey(query.updatedAt || query.updated_at || '');
      const matchesUpdatedDate = !updatedStartDateFilter || !updatedEndDateFilter || (
        Boolean(updatedDate)
        && updatedDate >= updatedStartDateFilter
        && updatedDate <= updatedEndDateFilter
      );

      return matchesSearch && matchesType && matchesAssignee && matchesUpdatedDate;
    })
  ), [queries, searchTerm, typeFilter, assigneeFilter, updatedEndDateFilter, updatedStartDateFilter]);

  const totalPages = serverPaginated
    ? Math.max(1, serverTotalPages)
    : Math.max(1, Math.ceil(filteredQueries.length / PAGE_SIZE));
  const pagedQueries = useMemo(() => {
    if (serverPaginated) return filteredQueries;
    return filteredQueries.slice(0, page * PAGE_SIZE);
  }, [filteredQueries, page, serverPaginated]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    if (serverPaginated || loading) return;
    if (page >= totalPages) return;
    const timer = window.setTimeout(() => {
      setPage((prev) => Math.min(totalPages, prev + 1));
    }, 80);
    return () => window.clearTimeout(timer);
  }, [serverPaginated, loading, page, totalPages]);

  const uniqueAssignees = Array.from(
    new Map(
      queries
        .filter(q => q.assignedTo)
        .map(q => [q.assignedTo!.id, q.assignedTo!])
    ).values()
  );

  const newItems = useMemo(
    () => pagedQueries.filter(q => getQueryLane(q.status) === 'NEW'),
    [pagedQueries]
  );
  const quotationSharedItems = useMemo(
    () => pagedQueries.filter(q => getQueryLane(q.status) === 'QUOTATION SHARED'),
    [pagedQueries]
  );
  const closedItems = useMemo(
    () => pagedQueries.filter(q => getQueryLane(q.status) === 'CLOSED'),
    [pagedQueries]
  );
  const bookedItems = useMemo(
    () => pagedQueries.filter(q => getQueryLane(q.status) === 'BOOKED'),
    [pagedQueries]
  );

  const [, setDraggingId] = useState<number | null>(null);
  const [lanePositions, setLanePositions] = useState<Record<string, number[]>>({});

  // Initialize and maintain lane order positions without overriding manual reorders
  useEffect(() => {
    setLanePositions(prev => {
      const next: Record<string, number[]> = { ...prev };
      const apply = (status: string, arr: Query[]) => {
        const existing = next[status] || [];
        const ids = arr.map(q => q.id);
        // keep existing order for ids that remain
        const kept = existing.filter(id => ids.includes(id));
        // append new ids in current visible order
        const appended = ids.filter(id => !kept.includes(id));
        next[status] = [...kept, ...appended];
      };
      apply('NEW', newItems);
      apply('QUOTATION SHARED', quotationSharedItems);
      apply('CLOSED', closedItems);
      apply('BOOKED', bookedItems);
      return next;
    });
  }, [newItems, quotationSharedItems, closedItems, bookedItems]);

  const orderLane = (items: Query[], status: string) => {
    const order = lanePositions[status] || [];
    const orderIndex = new Map(order.map((id, idx) => [id, idx] as [number, number]));
    return items.slice().sort((a, b) => {
      const ai = orderIndex.has(a.id) ? (orderIndex.get(a.id) as number) : Number.MAX_SAFE_INTEGER;
      const bi = orderIndex.has(b.id) ? (orderIndex.get(b.id) as number) : Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });
  };

  const workedTodayQueries = pagedQueries.filter(isWorkedToday);
  const toBeWorkedQueries = pagedQueries.filter((query) => !isWorkedToday(query));

  const applyDropChange = async (status: QueryLane, id: number, beforeId?: number | null) => {
    setLanePositions(prev => {
      const next = { ...prev } as Record<string, number[]>;
      Object.keys(next).forEach(k => {
        next[k] = (next[k] || []).filter(x => x !== id);
      });
      const lane = status;
      const arr = next[lane] ? [...next[lane]] : [];
      let insertAt = 0;
      if (beforeId && arr.includes(beforeId)) {
        insertAt = arr.indexOf(beforeId);
      }
      arr.splice(insertAt, 0, id);
      next[lane] = arr;
      return next;
    });

    const nextStatus = getQueryStatusValueForLane(status);
    setQueries(prev => prev.map(q => (q.id === id ? { ...q, status: nextStatus } : q)));
    setActiveQuery(prev => (prev?.id === id ? { ...prev, status: nextStatus } : prev));
    setSelectedQueryForBooking(prev => (prev?.id === id ? { ...prev, status: nextStatus } : prev));
    setVendorQuotationQuery(prev => (prev?.id === id ? { ...prev, status: nextStatus } : prev));
    try {
      await updateQueryStatus(id, status);
    } catch (e) {
      const customerId = parseInt(searchParams.get('customerId') || '0');
      load(customerId || undefined);
    }
  };

  const handleDropTo = async (status: QueryLane, ev: React.DragEvent<HTMLElement>, beforeId?: number | null) => {
    ev.preventDefault();
    ev.stopPropagation(); // Prevent event from bubbling to parent handlers
    const idStr = ev.dataTransfer.getData('text/plain');
    const id = parseInt(idStr || '0');
    if (!id) return;

    // Find the query to check its current status
    const query = queries.find(q => q.id === id);
    const currentStatus = query ? getQueryLane(query.status || '') : 'NEW';
    
    // Prevent moving from BOOKED to other statuses
    if (currentStatus === 'BOOKED' && status !== 'BOOKED') {
      toast.error('Cannot move a booked query to another status');
      return;
    }
    
    const isMovingToBooked = status === 'BOOKED';
    const isStatusChange = currentStatus !== status;
    if (isMovingToBooked && isStatusChange) {
      await openBookingModal(id);
      return;
    }

    await applyDropChange(status, id, beforeId);
  };

  const getAssigneeName = (query: Query) =>
    query.assignedTo?.name ||
    allUsers.find(user => user.id === query.assigned_to)?.name ||
    'Unassigned';

  const getNextFollowupLabel = (queryId: number) => {
    const iso = nextFollowupMap[queryId];
    if (!iso) return '';
    const date = formatDate(iso);
    const time = formatTime(iso);
    return [date, time].filter(Boolean).join(' ');
  };

  function isWorkedToday(query: Query) {
    const updatedAt = query.updatedAt || query.updated_at || query.createdAt || '';
    if (!updatedAt) return false;
    const date = new Date(updatedAt);
    if (Number.isNaN(date.getTime())) return false;

    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const updatedIst = fmt.format(date);
    const todayIst = fmt.format(new Date());

    return updatedIst === todayIst;
  }

  const handleCardDragStart = (queryId: number, e: React.DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setData('text/plain', String(queryId));
    setDraggingId(queryId);
  };

  const handleCardDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleCardDrop = (lane: QueryLane, e: React.DragEvent<HTMLDivElement>, beforeId?: number | null) => {
    handleDropTo(lane, e, beforeId);
  };

  const renderLaneColumn = (
    lane: QueryLane,
    className: string,
    header: string,
    items: Query[],
  ) => (
    <div
      className={`kanban-column ${className}`}
      data-lane={lane}
      onDragOver={handleCardDragOver}
      onDrop={(e) => handleDropTo(lane, e)}
    >
      <div className="kanban-column-header">{header} ({items.length})</div>
      {orderLane(items, lane).map(q => (
        <QueryCard
          key={q.id}
          query={q}
          lane={lane}
          assigneeName={getAssigneeName(q)}
          role={role}
          onOpen={openQueryModal}
          nextFollowupLabel={getNextFollowupLabel(q.id)}
          formatDate={formatDate}
          onOpenCustomer={openCustomerModal}
          onAddFollowup={openCreateFollowUpModal}
          onShareQuotation={openShareQuotationModal}
          onViewHistory={viewFollowUps}
          onAssign={openAssignmentModal}
          onVendorRfq={vendorRfqEnabled ? openVendorQuotationModal : undefined}
          onDragStart={handleCardDragStart}
          onDragOver={handleCardDragOver}
          onDrop={handleCardDrop}
        />
      ))}
    </div>
  );

  return (
    <Layout>
      <div className="crm-page packages-page">
        <div className="packages-header">
          <div>
            <h2>Customer Queries</h2>
          </div>
          <div className="packages-actions">
            <button
              className="btn"
              onClick={() => {
                if (customerIdFromParams) {
                  setNewQueryCustomerId(customerIdFromParams);
                  setShowCreateQueryModal(true);
                } else {
                  setShowCustomerPicker(true);
                }
              }}
              title={customerIdFromParams ? 'Create new query for selected customer' : 'Pick a customer to create a query'}
            >
              <FaPlus /> Add Query
            </button>
          </div>
        </div>

        <div className="packages-filters queries-filters">
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Search by name, location, type, reference ID..."
          />
          <select onChange={(e) => setAssigneeFilter(e.target.value)} value={assigneeFilter} className="filter-select" aria-label="Assignee">
            <option value="">All Assignees</option>
            {uniqueAssignees.map(user => (
              <option key={user.id} value={user.id}>{user.name}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn-secondary queries-date-filter"
            onClick={() => setShowDatePicker(true)}
            aria-label="Filter by updated date"
          >
            <FiCalendar />
            {updatedStartDateFilter && updatedEndDateFilter
              ? updatedStartDateFilter === updatedEndDateFilter
                ? formatFilterDate(updatedStartDateFilter)
                : `${formatFilterDate(updatedStartDateFilter)} – ${formatFilterDate(updatedEndDateFilter)}`
              : 'Updated dates'}
          </button>
          <button className="clear-btn" onClick={() => {
            setSearchTerm('');
            setTypeFilter('');
            setAssigneeFilter('');
            setUpdatedStartDateFilter('');
            setUpdatedEndDateFilter('');
            setSearchParams({});
            load();
          }}>Clear</button>
        </div>

        {showDatePicker && (
          <DateRangePicker
            startDate={updatedStartDateFilter}
            endDate={updatedEndDateFilter}
            onDateChange={(startDate, endDate) => {
              setUpdatedStartDateFilter(startDate);
              setUpdatedEndDateFilter(endDate);
            }}
            onClose={() => setShowDatePicker(false)}
          />
        )}

        <div className="queries-sections">
          <section className={`queries-section ${collapsedSections.workedToday ? 'is-collapsed' : ''}`}>
            <button
              type="button"
              className="queries-section__header"
              onClick={() => setCollapsedSections((prev) => ({ ...prev, workedToday: !prev.workedToday }))}
              aria-expanded={!collapsedSections.workedToday}
            >
              <span className="queries-section__title">Worked today</span>
              <span className="queries-section__count">{workedTodayQueries.length}</span>
            </button>
            {!collapsedSections.workedToday && (
              <div className="kanban-board queries-section__board">
                {renderLaneColumn('NEW', 'open', 'New', workedTodayQueries.filter((q) => getQueryLane(q.status) === 'NEW'))}
                {renderLaneColumn('QUOTATION SHARED', 'inprogress', 'Quotation shared', workedTodayQueries.filter((q) => getQueryLane(q.status) === 'QUOTATION SHARED'))}
                {renderLaneColumn('BOOKED', 'booked', 'Booked', workedTodayQueries.filter((q) => getQueryLane(q.status) === 'BOOKED'))}
                {renderLaneColumn('CLOSED', 'closed', 'Closed', workedTodayQueries.filter((q) => getQueryLane(q.status) === 'CLOSED'))}
              </div>
            )}
          </section>
          <section className={`queries-section ${collapsedSections.toBeWorked ? 'is-collapsed' : ''}`}>
            <button
              type="button"
              className="queries-section__header"
              onClick={() => setCollapsedSections((prev) => ({ ...prev, toBeWorked: !prev.toBeWorked }))}
              aria-expanded={!collapsedSections.toBeWorked}
            >
              <span className="queries-section__title">Others</span>
              <span className="queries-section__count">{toBeWorkedQueries.length}</span>
            </button>
            {!collapsedSections.toBeWorked && (
              <div className="kanban-board queries-section__board">
                {renderLaneColumn('NEW', 'open', 'New', toBeWorkedQueries.filter((q) => getQueryLane(q.status) === 'NEW'))}
                {renderLaneColumn('QUOTATION SHARED', 'inprogress', 'Quotation shared', toBeWorkedQueries.filter((q) => getQueryLane(q.status) === 'QUOTATION SHARED'))}
                {renderLaneColumn('BOOKED', 'booked', 'Booked', toBeWorkedQueries.filter((q) => getQueryLane(q.status) === 'BOOKED'))}
                {renderLaneColumn('CLOSED', 'closed', 'Closed', toBeWorkedQueries.filter((q) => getQueryLane(q.status) === 'CLOSED'))}
              </div>
            )}
          </section>
        </div>
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>

      {selectedCustomer && (
        <CustomerInfoModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      )}

      {showCreateFollowUpModal && (
        <CreateFollowUpModal
          queryId={queryId}
          required={followupRequired}
          onCreated={async () => {
            loadNextFollowups();
          }}
          backdropClassName="modal-level-2"
          onClose={() => {
            setShowCreateFollowUpModal(false);
            setFollowupRequired(false);
          }}
        />
      )}

      {showCreateQueryModal && newQueryCustomerId && (
        <CreateQueryModal
          customerId={newQueryCustomerId}
          onClose={() => setShowCreateQueryModal(false)}
          onQueryCreated={(newId) => {
            setShowCreateQueryModal(false);
            const refId = customerIdFromParams || newQueryCustomerId || 0;
            setNewQueryCustomerId(null);
            load(refId || undefined);
            setQueryId(newId);
            setFollowupRequired(true);
            setShowCreateFollowUpModal(true);
          }}
        />
      )}

      {showCustomerPicker && (
        <CustomerPickerModal
          onClose={() => setShowCustomerPicker(false)}
          onSelect={(c) => {
            setShowCustomerPicker(false);
            setNewQueryCustomerId(c.id!);
            setShowCreateQueryModal(true);
          }}
        />
      )}

      {showQueryHistory && (
        <QueryHistoryModal
          queryId={queryId}
          backdropClassName="modal-level-2"
          onClose={() => setShowQueryHistory(false)}
          onLog={(followupId) => {
            setShowQueryHistory(false);
            setFollowupId(followupId);
            setShowLogFollowupModal(true);
          }}
        />
      )}

      {showLogFollowupModal && (
        <LogFollowupModal
          queryId={queryId}
          followupId={followupId}
          onClose={() => {
            setShowLogFollowupModal(false);
            setFollowupId(0);
          }}
          onOutcomeAction={handleOutcomeAction}
        />
      )}

      {showAssignmentModal && (
        <AssignmentModal
          queryId={queryId}
          users={allUsers}
          backdropClassName="modal-level-2"
          onClose={() => setShowAssignmentModal(false)}
          onAssign={handleAssign}
        />
      )}

      {showCreateBookingModal && selectedQueryForBooking && (
        <AddBookingModal
          query={selectedQueryForBooking}
          onClose={() => {
            setShowCreateBookingModal(false);
            setSelectedQueryForBooking(null);
          }}
          onCreated={(alreadyMaterialized) => handleBookingCreated(selectedQueryForBooking, alreadyMaterialized)}
        />
      )}

      {showQueryModal && activeQuery && (
        <div className="modal-backdrop modal-level-1" onClick={() => setShowQueryModal(false)}>
          <div className="modal modal-lg query-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-actions">
                {vendorRfqEnabled && (
                  <button
                    className="btn"
                    type="button"
                    onClick={() => openVendorQuotationModal(activeQuery.id)}
                  >
                    + Vendor Quotation
                  </button>
                )}
              </div>
              <button className="close-btn" onClick={() => setShowQueryModal(false)}>&times;</button>
            </div>
            <div className="query-modal-content">
              <QueryCard
                query={activeQuery}
                lane={getQueryLane(activeQuery.status || '')}
                assigneeName={getAssigneeName(activeQuery)}
                role={role}
                variant="modal"
                nextFollowupLabel={getNextFollowupLabel(activeQuery.id)}
                formatDate={formatDate}
                onOpenCustomer={openCustomerModal}
                onAddFollowup={openCreateFollowUpModal}
                onShareQuotation={openShareQuotationModal}
                onViewHistory={viewFollowUps}
                onAssign={openAssignmentModal}
                onVendorRfq={vendorRfqEnabled ? openVendorQuotationModal : undefined}
              />
            </div>
          </div>
        </div>
      )}

      {showVendorQuotation && vendorQuotationQuery && (
        <VendorQuotationModal
          isOpen={showVendorQuotation}
          query={vendorQuotationQuery}
          backdropClassName="modal-level-2"
          onClose={() => {
            setShowVendorQuotation(false);
            setVendorQuotationQuery(null);
          }}
        />
      )}
    </Layout>
  );
};

export default QueriesPage;
